import { isoStringToJsDate } from '@/util/helpers/time-helpers';
import {
  TABLES_LOADED,
  VIEW_SAVED,
  VIEW_DELETED,
  LOADING,
  EXCEPTION,
  VIEWS_DATA_LOADED,
  SUGGESTIONS_LOADED,
  SUGGESTIONS_CLICKED,
  FLASH_MESSAGE,
  CHANGE_TAB,
  MAP_ENABLED,
  ALERT_MESSAGE,
  FEEDS_ON_OFF,
  NEW_WORLD_TOGGLE,
  USER_ACTIONS_LOADED,
  USER_ACTION_FIRED,
  ALERTS_ON_OFF,
  SHEETS_OTHER_TOOLS_ON_OFF,
  REFRESH_DASHBOARDS_5_MIN_ON_OFF,
  CHANGE_DATASOURCE,
  DATASOURCE_SELECTED,
} from '../actions/constants';
import TableRef from '../util/TableRef';
import ViewRef from '../util/ViewRef';
import SuggestedView from '../util/SuggestedView';
import { EXPLORER } from '../util/enums/views.constants';

const INITIAL_STATE = {
  datasourceId: null,
  userActions: null, // user actions for onboarding
  currentTab: EXPLORER, // default is HOME
  tables: null, // TableRef objs. null indicates that we're loading.  [] indicates there are no tables
  isReady: false, // indicates that datasource has finished loaded
  errorMsg: null, // error loading tables
  sqlViews: null, // list of the above tables that are actually SQL views
  views: null, // ViewRef objs
  masterTables: null, // TableRef objs representing the master tables for this datasource
  totalSavedViews: 999, // total number of views saved in datasource
  suggestions: [], // list of SuggestedView objs
  suggestionsClicked: false,
  name: null, // the name of the datasource
  isOwner: false, // true if the current user owns the current datasource
  isAdmin: false, // true if the current user is the owner or admin of this datasource
  isBilledByPanoply: false, // true if this datasource has a contract with Panoply (rather than us)
  userEmail: null, // string representing logged-in user's email address
  hideSqlEditor: true, // true if should hide sql editor from collaborators
  isHerokuBeta: false, // true if the billing for this datasource is controlled by heroku
  isHerokuAddonUser: false, // true if the current logged-in user is seeing the purple bar at the top
  isPublicUser: false, // true if current user is a public user (viewing public domain)
  isSelfHosted: false, // true if running self-hosted by customer
  s3UserArn: null, // s3 user that performs writes to external S3 buckets
  dbType: null, // EnumDbType
  features: [], // list of enabled feature flags
  maxSteps: 0, // max number of steps allowed in a query (UPDATE: this now actually represents the max number of saved queries allowed)
  trialEndsAt: null,
  tablesWithCreatedAt: [], // list of TableRef objs that contain created_at column
  mapEnabled: false, // true if should show Map tab to collaborators
  dashboardsEnabled: false, // true if show show dashboards tab
  configureEnabled: false, // true if datasource can configure selected tables/joins
  feedsDisabled: false, // true if feeds have been turned off by admin
  newWorld: false, // true if user has enabled new world
  isProPlus: false, // true if datasource has Pro+
  isLite: false, // true if datasource is 'lite'
  isCreditsPricing: false, // true if this datasource is eligible for credits pricing
  alertMessage: null, // alert message to show
  flashMessage: null, // flash message to show
  flashSeconds: null, // how long to show the message for (or -1 if forever)
  alertsDisabled: false,
  exportsDisabled: false,
  canRefreshDbFiveMinutesDisabled: true,
  isLimitedAccess: false,
};

const Datasource = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case LOADING:
      if (action.awaiting === TABLES_LOADED) {
        return {
          ...state,
          isReady: false, // causes loading icon to show (which, for example, forces MapContainer to reload itself)
          tables: null, // reset (so that loading icon will show up in left side bar)
          errorMsg: null, // reset
        };
      }
      return state;
    case EXCEPTION: {
      if (!state.tables) {
        // error while loading tables
        return { ...state, errorMsg: action.userMsg };
      }
      return state;
    }
    case TABLES_LOADED:
      return {
        ...state,
        name: action.payload._name,
        isReady: true,
        isOwner: action.payload._isOwner,
        isAdmin: action.payload._isAdmin,
        isPublic: action.payload.isPublic,
        isBilledByPanoply: action.payload._isBilledByPanoply,
        userEmail: action.payload._email,
        hideSqlEditor: action.payload._hideSqlEditor,
        mapEnabled: action.payload._mapEnabled,
        dashboardsEnabled: action.payload._dashboardsEnabled,
        feedsDisabled: action.payload._feedsDisabled,
        alertsDisabled: action.payload._alertsDisabled,
        exportsDisabled: action.payload._exportsDisabled,
        canRefreshDbFiveMinutesDisabled:
          action.payload._canRefreshDbFiveMinutesDisabled,
        newWorld: action.payload._isNewWorld,
        isProPlus: action.payload._isProPlus,
        isLite: action.payload._isLite,
        isCreditsPricing: action.payload._isCreditsPricing,
        isLimitedAccess: action.payload.isLimitedAccess,
        configureEnabled: action.payload._configureEnabled,
        isHerokuBeta: action.payload._isHerokuBeta,
        isHerokuAddonUser: action.payload._isHerokuAddonUser,
        isPublicUser: action.payload._isPublicUser,
        isSelfHosted: action.payload._isSelfHosted,
        s3UserArn: action.payload._s3UserArn,
        isGuest: action.payload._isGuest,
        dbType: action.payload._dbType,
        features: action.payload._features.map((f) => f),
        ownerFeatures: action.payload._ownerFeatures.map((f) => f),
        maxSteps: action.payload._maxSteps,
        trialEndsAt: isoStringToJsDate(action.payload._trialEndsAt),
        tables: action.payload._tables
          .map((t) => new TableRef(t._table._schema, t._table._name))
          .sort(sortFunction),
        masterTables: action.payload._tables
          .filter((t) => t._type === 'MASTER')
          .map((t) => new TableRef(t._table._schema, t._table._name))
          .sort(sortFunction),
        tablesWithCreatedAt: action.payload._tables
          .filter((t) => t._hasCreatedAt)
          .map((t) => new TableRef(t._table._schema, t._table._name)),
        sqlViews: action.payload._tables
          .filter((t) => t._type === 'VIEW')
          .map((t) => new TableRef(t._table._schema, t._table._name)),
        views: action.payload._views.map((v) => ViewRef.build(v)),
      };
    case USER_ACTIONS_LOADED: {
      const userActions = {};
      action.payload.map((_action) => (userActions[_action] = true));
      return { ...state, userActions };
    }
    case CHANGE_DATASOURCE: {
      return { ...INITIAL_STATE, datasourceId: action.payload.datasourceId };
    }
    // so far it works for public datasets only. After we move triggers to react components we can use it for the private page as well
    case DATASOURCE_SELECTED: {
      return { ...state, datasourceId: action.payload.datasourceId };
    }
    case USER_ACTION_FIRED: {
      if (!state.userActions) {
        // list of existing actions haven't yet come back from server
        return state;
      }
      return {
        ...state,
        userActions: { ...state.userActions, [action.payload.action]: true },
      };
    }
    case FEEDS_ON_OFF:
      return { ...state, feedsDisabled: !action.payload.on };
    case ALERTS_ON_OFF:
      return { ...state, alertsDisabled: !action.payload.on };
    case SHEETS_OTHER_TOOLS_ON_OFF:
      return { ...state, exportsDisabled: !action.payload.on };
    case REFRESH_DASHBOARDS_5_MIN_ON_OFF:
      return { ...state, canRefreshDbFiveMinutesDisabled: !action.payload.on };
    case NEW_WORLD_TOGGLE:
      return { ...state, newWorld: action.payload.on };
    case VIEW_SAVED:
    case VIEW_DELETED:
      return {
        ...state,
        views: action.payload.json.map((v) => ViewRef.build(v)),
      };
    case CHANGE_TAB: {
      return { ...state, currentTab: action.payload.tab };
    }
    case SUGGESTIONS_LOADED:
      return {
        ...state,
        suggestions: action.payload.map((s) => SuggestedView.build(s)), // list of strings
      };
    case SUGGESTIONS_CLICKED:
      return { ...state, suggestionsClicked: true };
    case ALERT_MESSAGE: {
      return { ...state, alertMessage: action.payload.message };
    }
    case FLASH_MESSAGE: {
      return {
        ...state,
        flashMessage: action.payload.message,
        flashSeconds: action.payload.seconds,
      };
    }
    case MAP_ENABLED: {
      return { ...state, mapEnabled: true };
    }
    case VIEWS_DATA_LOADED: {
      return {
        ...state,
        totalSavedViews: action.payload.json._totalSavedViews,
      };
    }
    default:
      return state;
  }
};

function sortFunction(a, b) {
  if (a.name() < b.name()) {
    return -1;
  }
  if (a.name() > b.name()) {
    return 1;
  }
  return 0;
}

export default Datasource;
